// src/components/accueil/CarteActionsFnct.tsx — SCE Issues
// « Vos actions FNC-T » à l'accueil (lot 5 du § 14, 22/10 ; maquette
// https://claude.ai/artifact/2nfLQyNb8UjEEBZs5VCmNm, artboards « Accueil ») :
// n'apparaît QUE si la personne a une action ouverte — l'opérateur sans action ne
// voit rien de nouveau. Les deux plus urgentes, avec Clore et Reporter (les ronds
// de fnct/GestesAction ; le formulaire s'ouvre sous la ligne, sans quitter
// l'accueil — 24/10, GesteActionEnPlace), puis « Toutes mes FNC-T ». Pendant la construction : seulement
// pour qui a accès aux FNC-T (accesFnct) — sinon l'accueil ne lit pas les listes.

import { useMemo, useState, type JSX } from 'react'
import { useNavigate } from 'react-router-dom'
import { CarteAccueil, PiedCarte } from '@/components/accueil/CarteAccueil'
import { useOuvrirFiche } from '@/components/layout/ModaleRoute'
import { useCurrentUser } from '@/hooks/useCurrentUser'
import { useFnct } from '@/hooks/useFnct'
import { accesFnct, gereActionsFnct } from '@/utils/droitsFnct'
import { FNCT_EN_ESSAI, FNCT_MASQUEE } from '@/config/fnct.config'
import { peutAgirSurAction } from '@/utils/actionsFnct'
import { lesPlusFrequents } from '@/utils/annuaire'
import { aujourdhuiParis } from '@/utils/fnct'
import { MentionAttenteBE } from '@/components/fnct/EtatsFnct'
import { GesteActionEnPlace, type GesteAction } from '@/components/fnct/GesteActionEnPlace'
import { GestesAction, PastilleEcheance } from '@/components/fnct/GestesAction'
import { lienFnct } from '@/utils/liensFiches'
import { mesActions, titrePourVous, type MonAction } from '@/utils/mesFnct'

const LIGNES = 2

/** `hauteurMax` (bureau) : la carte se serre dans sa colonne et son contenu défile. */
export function CarteActionsFnct({ hauteurMax }: { hauteurMax?: string }): JSX.Element | null {
  const { user, isFournisseur } = useCurrentUser()
  if (!user || isFournisseur || FNCT_MASQUEE || !accesFnct(user.upn)) return null
  return <Carte hauteurMax={hauteurMax} />
}

function Carte({ hauteurMax }: { hauteurMax?: string }): JSX.Element | null {
  const { user, nomReel, enVueRole } = useCurrentUser()
  const { fncts, actions, recharger } = useFnct()
  // Clore / Reporter ouvert sous une ligne.
  const [ouverte, setOuverte] = useState<{ id: string; geste: GesteAction } | null>(null)
  const navigate = useNavigate()
  const ouvrirFiche = useOuvrirFiche()
  const aujourdhui = aujourdhuiParis()
  // La personne AFFICHÉE (bascule de rôle comprise) : on voit l'accueil qu'elle verrait.
  const nom = user ? (enVueRole ? user.nom : nomReel || user.nom) : ''
  const upn = user?.upn ?? ''
  const m = useMemo(() => mesActions(fncts, actions, { nom, upn }, aujourdhui), [fncts, actions, nom, upn, aujourdhui])
  if (!user) return null
  const toutes = [...m.retard, ...m.semaine, ...m.plusTard]
  if (toutes.length === 0) return null
  const gestionnaire = gereActionsFnct(user.upn)
  const carte = (
    <CarteAccueil
      ajuste={!!hauteurMax}
      titre="Vos actions FNC-T"
      compteur={toutes.length}
      sousTitre={FNCT_EN_ESSAI ? `${titrePourVous(m)} · phase de test, non officiel` : titrePourVous(m)}
      pied={<PiedCarte libelle="Toutes mes FNC-T" onClick={() => navigate('/fnct?vue=mes')} />}
    >
      {toutes.slice(0, LIGNES).map((x) => (
        <div key={x.action.id}>
          <Ligne
            x={x}
            aujourdhui={aujourdhui}
            // En bascule de rôle aussi, comme partout (30/10) : chaque écriture y est confirmée.
            agir={peutAgirSurAction(nom, x.action, gestionnaire) && !!x.action.itemId}
            geste={ouverte?.id === x.action.id ? ouverte.geste : null}
            onGeste={(geste) => setOuverte(ouverte?.id === x.action.id && ouverte.geste === geste ? null : { id: x.action.id, geste })}
            onOuvrir={() => ouvrirFiche(lienFnct(x.fnct.numero))}
          />
          {ouverte?.id === x.action.id && (
            <GesteActionEnPlace action={x.action} geste={ouverte.geste} habitues={lesPlusFrequents(actions.map((a) => a.responsable))} onFermer={() => setOuverte(null)} onFait={recharger} />
          )}
        </div>
      ))}
    </CarteAccueil>
  )
  return hauteurMax ? <div style={{ flexShrink: 0, maxHeight: hauteurMax, display: 'flex', flexDirection: 'column', minHeight: 0 }}>{carte}</div> : carte
}

function Ligne({
  x,
  aujourdhui,
  agir,
  geste,
  onGeste,
  onOuvrir,
}: {
  x: MonAction
  aujourdhui: string
  agir: boolean
  geste: GesteAction | null
  onGeste: (geste: GesteAction) => void
  onOuvrir: () => void
}): JSX.Element {
  const machines = x.fnct.numerosSerie.length > 1 ? `${x.fnct.numerosSerie[0]} + ${x.fnct.numerosSerie.length - 1}` : (x.fnct.numerosSerie[0] ?? '')
  return (
    <div className="flex items-center" style={{ gap: 8, padding: '12px 10px 12px 18px', borderBottom: '1px solid var(--color-gray-10)' }}>
      <button type="button" onClick={onOuvrir} className="flex flex-col" style={{ flex: 1, minWidth: 0, gap: 4, border: 'none', background: 'none', padding: 0, textAlign: 'left', fontFamily: 'inherit', color: 'var(--color-black)', cursor: 'pointer' }}>
        <span lang="en" style={{ fontSize: 15, lineHeight: '20px', overflowWrap: 'anywhere' }}>
          {x.action.description}
        </span>
        <span className="flex flex-wrap items-center" style={{ gap: '4px 8px' }}>
          <PastilleEcheance echeance={x.echeance} retard={x.retard} reportee={!!x.action.echeanceReportee} aujourdhui={aujourdhui} petite />
          <span style={{ fontSize: 13, lineHeight: '18px', color: 'var(--color-text-secondary)', fontVariantNumeric: 'tabular-nums' }}>
            {x.fnct.numero}
            {machines ? ` · ${machines}` : ''}
          </span>
        </span>
        {x.attendBE && <MentionAttenteBE />}
      </button>
      {agir && <GestesAction ouvert={geste} onGeste={onGeste} />}
    </div>
  )
}
